import { useState, useEffect, useCallback } from 'react';
import { McParameterDetailResponseDto } from 'src/api/references/referenceServicesDtos';
import { referenceService } from 'src/api/references/referenceService';
import { useTranslate } from 'src/locales/use-locales';

//479214 maddesi için mcParameterDetaillerde muulti çekimde selectlerden bağımsız çalışması için global cache kullanılıyor.

let globalMcpCache: Record<number, McParameterDetailResponseDto[]> = {};
let pendingRequests: Set<number> = new Set();
let ongoingRequestPromises: Map<string, Promise<void>> = new Map();

export const useMcpCache = (mcpIds: number[]) => {
  const { t } = useTranslate();
  const [cacheLoaded, setCacheLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{ message: string; open: boolean } | null>(null);

  const loadBulkMcpData = useCallback(async (ids: number[]) => {
    // 0 değerlerini filtrele (API 0'dan büyük değerler istiyor)
    const validIds = ids.filter(id => id > 0);
    
    if (validIds.length === 0) {
      // Geçerli ID yoksa hiçbir şey yapma
      return;
    }
    
    // Eksik olan MCP ID'lerini bul (cache'de olmayan ve pending'de olmayan)
    const missingIds = validIds.filter(id => {
      const cached = globalMcpCache[id];
      const hasCache = cached && Array.isArray(cached) && cached.length > 0;
      const isPending = pendingRequests.has(id);
      const shouldLoad = !hasCache && !isPending;

      return shouldLoad;
    });

    if (missingIds.length === 0) {
      // Tüm ID'ler zaten cache'de veya fetch ediliyor
      return;
    }

    // Bu ID'leri pending olarak işaretle
    missingIds.forEach(id => pendingRequests.add(id));

    // Request için benzersiz bir key oluştur
    const requestKey = missingIds.sort().join(',');

    // Aynı ID'ler için zaten bir request varsa onu bekle
    if (ongoingRequestPromises.has(requestKey)) {
      return ongoingRequestPromises.get(requestKey);
    }

    setLoading(true);

    const requestPromise = (async () => {
      try {
        const response = await referenceService.mcParameterDetail.getBulkMcParameterDetails({
          mcpIds: missingIds
        });

        if (response.issuccess && response.data) {
          response.data.forEach(item => {
            const convertedDetails: McParameterDetailResponseDto[] = item.mcparameterdetails.map((detail: any) => ({
              id: String(detail.id),
              mcpId: detail.mcp_id ?? item.mcp_id,
              name: detail.name,
              note: detail.note ?? null,
              value: detail.value,
              orderNumber: detail.order_number ?? null
            }));

            globalMcpCache[item.mcp_id] = convertedDetails;
            pendingRequests.delete(item.mcp_id);
          });
        } else {
          console.warn('[useMcpCache] Response not successful or no data:', response);
        }
      } catch (error) {
        console.error("Error loading global MCP cache:", error);
        setError({ message: t("common.error-loading-mcp-details"), open: true });
        // Hata durumunda pending'leri temizle ki tekrar denenebilsin
        missingIds.forEach(id => pendingRequests.delete(id));
      } finally {
        setLoading(false);
        ongoingRequestPromises.delete(requestKey);
      }
    })();

    ongoingRequestPromises.set(requestKey, requestPromise);
    return requestPromise;
  }, [t]);

  useEffect(() => {
    // 0 değerlerini filtrele (sadece geçerli ID'ler için cache kontrolü yap)
    const validIds = mcpIds.filter(id => id > 0);
    
    if (validIds.length > 0) {
      loadBulkMcpData(mcpIds).then(() => {
        // Tüm geçerli ID'ler cache'de mi kontrol et (boş array değil, dolu array olmalı)
        const allLoaded = validIds.every(id => {
          const cached = globalMcpCache[id];
          return cached && cached.length > 0;
        });
        setCacheLoaded(allLoaded);
      }).catch(() => {
        // Hata durumunda cache yüklenmedi sayılır
        setCacheLoaded(false);
      });
    } else {
      // Geçerli ID yoksa cache yüklü sayılır
      setCacheLoaded(true);
    }
  }, [mcpIds, loadBulkMcpData]);

  const getMcpParameterDetails = useCallback((mcpId: number): McParameterDetailResponseDto[] => {
    const cached = globalMcpCache[mcpId] || [];
    return cached;
  }, []);

  return {
    cacheLoaded,
    loading,
    error,
    setError,
    getMcpParameterDetails
  };
};
